import { HttpErrorResponse } from "@angular/common/http";
import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { ClerkService } from "ngx-clerk-iliad";
import { catchError, delayWhen, filter, first, firstValueFrom, from, iif, map, of, switchMap, timeout } from "rxjs";
import { ApiSignInFailureKind } from "../../../../apiClient/models";
import { SignInClient } from "../../../../apiClient/services";
import { AppUrlsService } from "../../../../services/app-urls.service";
import { NavigationService } from "../../../../services/navigation.service";
import { reportToSentry } from "../../../../shared/sentry/reportFunctions";
import { AppFullPageLoaderComponent } from "../../../../shared/ui/components/full-page-loader/full-page-loader";
import { getTranslatedSignInFailure, isCompleteSignInError } from "../../sign-in.types";

@Component({
  selector: "app-sign-in-redirect",
  template: `<app-full-page-loader [statusText]="statusText"></app-full-page-loader>`,
  imports: [AppFullPageLoaderComponent],
})
export class SignInRedirectComponent {
  public statusText = "";

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly translocoService: TranslocoService,
    private readonly signInClient: SignInClient,
    private readonly clerkService: ClerkService,
    private readonly navigationService: NavigationService,
    private readonly appUrlsService: AppUrlsService,
  ) {
    this.statusText = this.translocoService.translate("app.sign-in-redirect.loading");

    const destUrlString = this.activatedRoute.snapshot.queryParamMap.get("dest");
    // This component is always rendered on the primary domain (i.e. rantalainen.fi), but the user's destination
    // might be a satellite domain (i.e. enora.no).
    let destinationIsSatellite = false;
    let destUrl = "/";
    if (destUrlString) {
      try {
        const url = new URL(destUrlString);
        // If we're on the primary domain, we can do a direct router-based navigate
        if (url.origin === window.location.origin) {
          // Angular's router needs relative paths, so strip out the protocol and domain name.
          destUrl = url.pathname + url.search + url.hash;
        } else if (this.appUrlsService.allowedOrigins.includes(url.origin)) {
          // Our destination is on a satellite domain, so we need to do a direct reassignment of window.location down below
          destUrl = url.href;
          destinationIsSatellite = true;
        }
      } catch (err) {
        reportToSentry(err, "Auth.SignIn");
        destUrl = "/";
      }
    }

    // Only get the firstValueFrom the session$, because reloading it down below
    // will cause a new emission, which would put us into an infinite loop if we just subscribed
    // normally, even with a first() filter.
    firstValueFrom(
      this.clerkService.session$.pipe(
        // Wait for session to finish loading, because we can redirect to this page fast enough
        // that if we try to use clerkService.session$ immediately, it won't be ready.
        // (If session === undefined, that indicates that the session is loading, per Clerk docs.)
        filter((session) => session !== undefined && session !== null),
        timeout({ first: 5_000 }),
      ),
    )
      .then((session) => {
        this.signInClient
          .completeSignIn()
          .pipe(
            // If response value indicates we should, reload the session to get any new metadata that
            // completeSignIn() added.
            switchMap((signInResult) =>
              iif(
                () => signInResult.success && signInResult.shouldReloadSession,
                of(signInResult).pipe(delayWhen(() => from(session.reload()))),
                of(signInResult),
              ),
            ),
            catchError((error) => {
              reportToSentry(error, "Auth.SignIn");

              let statusCode: number | null = null;
              let reason: ApiSignInFailureKind = ApiSignInFailureKind.UnableToCompleteSignIn;
              if (error instanceof HttpErrorResponse) {
                statusCode = error.status;
                if (isCompleteSignInError(error.error)) {
                  reason = error.error.reason;
                }
              }

              // If sign-in completion failed, end the Clerk session so we're not partially signed-in.
              // Don't use the top-level clerk.signOut(), as that will try to redirect.
              return from(session.end()).pipe(
                // Session might no longer exist by the time we get here, so swallow and log any errors when trying to close it.
                catchError((e) => reportToSentry(e, "Auth.SignIn")),
                map(() => ({
                  success: false,
                  statusCode: statusCode,
                  reason: reason,
                })),
              );
            }),
            first(),
          )
          .subscribe({
            next: (result) => {
              if (!result.success) {
                const reasonFragment = getTranslatedSignInFailure(this.translocoService, result.reason);
                const httpFragment =
                  "statusCode" in result && result.statusCode !== null ? `HTTP ${result.statusCode}. ` : "";
                const errorDetails = `${httpFragment}${reasonFragment}`;
                const errorString = this.translocoService.translate<string>("app.sign-in-redirect.error", {
                  errorDetails,
                });

                this.navigationService.navigateToErrorPage(errorString);
                return;
              }

              if (destinationIsSatellite) {
                window.location.href = destUrl;
              } else {
                this.router.navigateByUrl(destUrl);
              }
            },
          });
      })
      .catch((error) => {
        // Failed to get session or timed out waiting for it to load
        // Also happens when user manages to refresh the "signInRedirect" page without a proper session
        // Just throw them to root which will redirect to sign-in if needed

        reportToSentry(error, "Auth.SignIn");
        this.router.navigateByUrl("/");
      });
  }
}
